import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { extractErrorMessage } from '../utils/errorHandling';
import LoadingSpinner from './LoadingSpinner';
import ErrorDisplay from './ErrorDisplay';
import CollapsibleFilters from './CollapsibleFilters';

/**
 * RTO (Return To Origin) management.
 *
 * RTO is distinct from NDR: an NDR is a failed delivery *attempt* (parcel still
 * out for delivery), whereas an RTO is the parcel being *sent back to origin*.
 * RTOs are stored as OrderReturn rows with return_type='rto' and served by the
 * returns API (GET /returns?return_type=rto). This view pulls those records.
 */

interface RTOItem {
  id: number;
  product_id: number;
  product_sku: string | null;
  product_name: string | null;
  quantity: number;
  condition: string;
  notes: string | null;
}

interface RTORecord {
  id: number;
  return_number: string;
  order_id: number;
  order_number: string | null;
  return_type: string;
  reason: string | null;
  status: string;
  awb_number: string | null;
  received_date: string | null;
  restock: boolean;
  warehouse: string | null;
  notes: string | null;
  created_at: string | null;
  items: RTOItem[];
}

interface RTOManagementProps {
  userRole?: string;
}

// OrderReturn.status lifecycle: initiated -> received -> inspected ->
// restocked | refunded | disposed
const STATUS_BADGE: Record<string, { color: string; bg: string; label: string }> = {
  initiated: { color: '#856404', bg: '#fff3cd', label: 'Initiated' },
  received: { color: '#004085', bg: '#cce5ff', label: 'Received' },
  inspected: { color: '#0c5460', bg: '#d1ecf1', label: 'Inspected' },
  restocked: { color: '#155724', bg: '#d4edda', label: 'Restocked' },
  refunded: { color: '#6b21a8', bg: '#f3e8ff', label: 'Refunded' },
  disposed: { color: '#721c24', bg: '#f8d7da', label: 'Disposed' },
};

const STATUS_OPTIONS = Object.keys(STATUS_BADGE);

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const s = STATUS_BADGE[status] || { color: '#383d41', bg: '#d6d8db', label: status };
  return (
    <span style={{
      display: 'inline-block', padding: '4px 10px', borderRadius: '12px',
      fontSize: '11px', fontWeight: 600, color: s.color, background: s.bg, whiteSpace: 'nowrap',
    }}>{s.label}</span>
  );
};

const fmtDate = (iso: string | null) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
};

const getDateNDaysAgo = (days: number): string => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().split('T')[0];
};

const RTOManagement: React.FC<RTOManagementProps> = ({ userRole = 'viewer' }) => {
  const [records, setRecords] = useState<RTORecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);

  // Filters. Default to the last month; full history is available via Reports.
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState(getDateNDaysAgo(30));
  const [dateTo, setDateTo] = useState('');

  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const fetchRTOs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, any> = {
        return_type: 'rto',
        skip: page * pageSize,
        limit: pageSize,
      };
      if (statusFilter) params.status = statusFilter;
      if (search.trim()) params.q = search.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const res = await api.get('/returns/', { params });
      const data = res.data || {};
      setRecords(Array.isArray(data.returns) ? data.returns : []);
      setTotal(typeof data.total === 'number' ? data.total : 0);
    } catch (e: any) {
      setError(extractErrorMessage(e));
      setRecords([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search, dateFrom, dateTo, page, pageSize]);

  useEffect(() => {
    fetchRTOs();
  }, [fetchRTOs]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Filters */}
      <CollapsibleFilters label="Filters">
        <div style={{ padding: '12px 20px', background: '#f8f9fa', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 240px' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>
              Search
            </label>
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              placeholder="Return #, AWB, reason..."
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', background: 'white' }}
            />
          </div>
          <div style={{ flex: '0 0 200px' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', background: 'white' }}
            >
              <option value="">All Statuses</option>
              {STATUS_OPTIONS.map(s => (
                <option key={s} value={s}>{STATUS_BADGE[s].label}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: '0 0 150px' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>
              From
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', background: 'white' }}
            />
          </div>
          <div style={{ flex: '0 0 150px' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>
              To
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', background: 'white' }}
            />
          </div>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => { setStatusFilter(''); setSearch(''); setDateFrom(getDateNDaysAgo(30)); setDateTo(''); setPage(0); }}
          >
            Clear
          </button>
          <button className="btn btn-secondary btn-sm" onClick={fetchRTOs}>
            Refresh
          </button>
        </div>
      </CollapsibleFilters>

      {/* Content */}
      <div className="table-container" style={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <LoadingSpinner />
        ) : error ? (
          <ErrorDisplay error={error} onRetry={fetchRTOs} />
        ) : records.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>
            No RTO (Return To Origin) records found.
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: '40px' }}></th>
                <th>Return #</th>
                <th>Order #</th>
                <th>AWB</th>
                <th>Status</th>
                <th>Reason</th>
                <th>Warehouse</th>
                <th>Restock</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) => (
                <React.Fragment key={r.id}>
                  <tr
                    style={{ cursor: 'pointer' }}
                    onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                  >
                    <td style={{ textAlign: 'center', color: '#94a3b8' }}>
                      {expanded === r.id ? '▾' : '▸'}
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{r.return_number}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{r.order_number || '—'}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{r.awb_number || '—'}</td>
                    <td><StatusBadge status={r.status} /></td>
                    <td style={{ fontSize: '13px', maxWidth: '240px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.reason || ''}>
                      {r.reason || '—'}
                    </td>
                    <td style={{ fontSize: '13px' }}>{r.warehouse || '—'}</td>
                    <td style={{ fontSize: '13px' }}>{r.restock ? 'Yes' : 'No'}</td>
                    <td style={{ fontSize: '13px', color: '#64748b' }}>{fmtDate(r.created_at)}</td>
                  </tr>
                  {expanded === r.id && (
                    <tr>
                      <td colSpan={9} style={{ background: '#f8fafc', padding: '12px 20px' }}>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Returned Items ({r.items.length})
                        </div>
                        {r.items.length === 0 ? (
                          <div style={{ fontSize: '13px', color: '#94a3b8' }}>No item details.</div>
                        ) : (
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                            <thead>
                              <tr style={{ background: '#f1f5f9' }}>
                                <th style={{ textAlign: 'left', padding: '6px 10px' }}>SKU</th>
                                <th style={{ textAlign: 'left', padding: '6px 10px' }}>Product</th>
                                <th style={{ textAlign: 'right', padding: '6px 10px' }}>Qty</th>
                                <th style={{ textAlign: 'left', padding: '6px 10px' }}>Condition</th>
                              </tr>
                            </thead>
                            <tbody>
                              {r.items.map((it) => (
                                <tr key={it.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                  <td style={{ padding: '6px 10px', fontFamily: 'monospace', fontSize: '12px' }}>{it.product_sku || '—'}</td>
                                  <td style={{ padding: '6px 10px' }}>{it.product_name || '—'}</td>
                                  <td style={{ padding: '6px 10px', textAlign: 'right' }}>{it.quantity}</td>
                                  <td style={{ padding: '6px 10px', textTransform: 'capitalize' }}>{it.condition}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                        {r.notes && (
                          <div style={{ marginTop: '8px', fontSize: '13px', color: '#475569' }}>
                            <strong>Notes:</strong> {r.notes}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {!loading && !error && total > 0 && (
        <div style={{ padding: '10px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', background: 'white' }}>
          <div style={{ fontSize: '12px', color: '#64748b' }}>
            Showing {total === 0 ? 0 : page * pageSize + 1} to {Math.min((page + 1) * pageSize, total)} of {total}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
              style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '12px', background: 'white' }}
            >
              {[25, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
            </select>
            <button className="btn btn-secondary btn-sm" disabled={page <= 0} onClick={() => setPage(p => Math.max(0, p - 1))}>
              Previous
            </button>
            <span style={{ fontSize: '12px', color: '#334155' }}>Page {page + 1} of {totalPages}</span>
            <button className="btn btn-secondary btn-sm" disabled={page + 1 >= totalPages} onClick={() => setPage(p => p + 1)}>
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RTOManagement;
